/**
 * TaskMap — field-staff map view (Iter 130)
 *
 * • Auto-locks onto the staff's current GPS (start point marker) and
 *   watches subsequent positions live via navigator.geolocation.watchPosition.
 * • Nearest-neighbour route optimizer (haversine) + total straight-line
 *   distance vs actual route distance from Google Directions API.
 * • Auto-opens the current task when the staff walks within
 *   PROXIMITY_M meters of the customer's GPS.
 * • Fires a route-deviation alert (toast + backend office alarm) when
 *   distance to the next stop increases over 3 consecutive updates.
 *
 * Google Maps JS API is lazy-loaded via a plain <script> tag so we don't
 * pull in a whole npm dependency for one page.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  MapTrifold, ListBullets, Path, NavigationArrow, MapPin, Truck,
  CircleNotch, Warning,
} from "@phosphor-icons/react";
import { staffApi } from "../../lib/staffApi";
const GMAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

// Threshold to consider the staff "at" the customer stop → auto-open task.
const PROXIMITY_M = 60;
// Trend increases over N pings before we treat it as "moving away".
const DIVERGE_STREAK = 3;
// Minimum delta between pings before we bother computing a trend (meters).
const MOVE_EPSILON_M = 15;

// -------- Google Maps loader --------
let _mapsPromise = null;
function loadGoogleMaps() {
  if (_mapsPromise) return _mapsPromise;
  _mapsPromise = new Promise((resolve, reject) => {
    if (typeof window === "undefined") return reject("no window");
    if (window.google?.maps) return resolve(window.google.maps);
    if (!GMAPS_KEY) return reject("REACT_APP_GOOGLE_MAPS_API_KEY missing");
    const s = document.createElement("script");
    s.src = `https://maps.googleapis.com/maps/api/js?key=${GMAPS_KEY}&libraries=geometry`;
    s.async = true;
    s.defer = true;
    s.onload = () => resolve(window.google.maps);
    s.onerror = () => reject("Google Maps failed to load");
    document.head.appendChild(s);
  });
  return _mapsPromise;
}

// -------- Haversine (km) --------
function haversineKm(a, b) {
  const R = 6371;
  const dLat = ((b.lat - a.lat) * Math.PI) / 180;
  const dLng = ((b.lng - a.lng) * Math.PI) / 180;
  const la1 = (a.lat * Math.PI) / 180;
  const la2 = (b.lat * Math.PI) / 180;
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(la1) * Math.cos(la2) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
}

// Greedy nearest-neighbour tour starting from `start`. Simple, deterministic.
function optimizeRoute(start, stops) {
  const remaining = [...stops];
  const ordered = [];
  let cursor = start;
  while (remaining.length) {
    let bestIdx = 0;
    let bestKm = Infinity;
    for (let i = 0; i < remaining.length; i++) {
      const d = haversineKm(cursor, remaining[i]);
      if (d < bestKm) { bestKm = d; bestIdx = i; }
    }
    const next = remaining.splice(bestIdx, 1)[0];
    ordered.push({ ...next, leg_km: bestKm });
    cursor = next;
  }
  return ordered;
}

export default function TaskMap({ tasks, onOpenTask }) {
  const mapDivRef = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef([]);
  const nearbyMarkersRef = useRef([]);
  const polylineRef = useRef(null);
  const staffMarkerRef = useRef(null);
  const watchIdRef = useRef(null);
  const lastAutoOpenRef = useRef(null);        // task.id last auto-opened
  const distanceHistoryRef = useRef([]);       // rolling km list for trend
  const [mapsReady, setMapsReady] = useState(false);
  const [staffPos, setStaffPos] = useState(null);
  const [locating, setLocating] = useState(false);
  const [optimized, setOptimized] = useState(null);
  const [routeKm, setRouteKm] = useState(null); // Google Directions API result
  const [routeMin, setRouteMin] = useState(null);
  const [routeLoading, setRouteLoading] = useState(false);
  const [tripStarted, setTripStarted] = useState(false);
  const [liveTrack, setLiveTrack] = useState(false);
  // Iter 170 — nearby overlays.
  const [layers, setLayers] = useState({
    staff: true, drops: true, godowns: true, customers: true,
  });
  const [radiusKm, setRadiusKm] = useState(5);
  const [nearby, setNearby] = useState({
    field_staff: [], drop_locations: [], godowns: [], customers: [],
  });
  // Iter 172 — Focus Near Me: when ON, map fits only the nearby-pin
  // cluster around the staff instead of the full route.
  const [focusNearMe, setFocusNearMe] = useState(false);

  // Only consider tasks that (a) have GPS on the customer AND (b) are
  // still actionable — completed / missed / cancelled stops don't belong
  // on the active route.
  const eligible = useMemo(() => {
    return (tasks || [])
      .filter(t => ["pending", "in_progress"].includes(t.status))
      .map(t => {
        const c = t.customer || {};
        return c.latitude && c.longitude
          ? {
              id: t.id,
              lat: Number(c.latitude),
              lng: Number(c.longitude),
              name: c.name || "Customer",
              phone: c.phone || "",
              address: c.address || "",
              request_no: t.sr_request_no,
              priority: t.sr_priority || t.priority,
              status: t.status,
              nav_url: t.nav_url,
              task: t,
            }
          : null;
      })
      .filter(Boolean);
  }, [tasks]);

  const displayOrder = optimized || eligible;

  // Straight-line total (haversine) — includes leg from staff to stop #1.
  const straightKm = useMemo(() => {
    if (!displayOrder.length) return 0;
    let total = 0;
    let cursor = staffPos || displayOrder[0];
    for (const s of displayOrder) {
      total += haversineKm(cursor, s);
      cursor = s;
    }
    return total;
  }, [displayOrder, staffPos]);

  // -------- Load Maps JS + create map --------
  useEffect(() => {
    let cancelled = false;
    loadGoogleMaps()
      .then(() => { if (!cancelled) setMapsReady(true); })
      .catch(err => { if (!cancelled) toast.error(String(err)); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!mapsReady || !mapDivRef.current || mapRef.current) return;
    const center = staffPos
      || (eligible[0] ? { lat: eligible[0].lat, lng: eligible[0].lng } : { lat: 10.0, lng: 76.3 });
    mapRef.current = new window.google.maps.Map(mapDivRef.current, {
      center, zoom: 13,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: true,
      gestureHandling: "greedy",
    });
  }, [mapsReady, staffPos, eligible]);

  // -------- One-shot locate --------
  const locate = useCallback(() => {
    if (!navigator.geolocation) {
      toast.error("Geolocation not supported on this device");
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      pos => {
        setStaffPos({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        setLocating(false);
      },
      err => {
        toast.error(`Location denied: ${err.message}`);
        setLocating(false);
      },
      { enableHighAccuracy: true, timeout: 8000, maximumAge: 30000 },
    );
  }, []);

  useEffect(() => { locate(); }, [locate]);

  // Iter 170 — pull nearby overlays whenever staff GPS or radius changes.
  useEffect(() => {
    if (!staffPos) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await staffApi.get(
          `/staff-app/nearby?lat=${staffPos.lat}&lng=${staffPos.lng}&radius_km=${radiusKm}`,
        );
        if (!cancelled) setNearby(r.data || {
          field_staff: [], drop_locations: [], godowns: [], customers: [],
        });
      } catch { /* silent — non-critical overlay */ }
    })();
    return () => { cancelled = true; };
  }, [staffPos, radiusKm]);

  // -------- Always-on live self-tracking (Iter 172) --------
  // The self marker used to update only when a trip was started. Ops
  // asked for a persistent self-live-location so the pin follows the
  // staff continuously while the tasks tab is open.
  useEffect(() => {
    if (!navigator.geolocation) return undefined;
    if (liveTrack) return undefined; // already watched by the trip effect
    const id = navigator.geolocation.watchPosition(
      pos => setStaffPos({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      () => {}, // silent — the tripStarted effect surfaces errors
      { enableHighAccuracy: true, timeout: 12000, maximumAge: 8000 },
    );
    return () => navigator.geolocation.clearWatch(id);
  }, [liveTrack]);

  // -------- Live tracking (only while a trip is started) --------
  useEffect(() => {
    if (!liveTrack || !navigator.geolocation) return undefined;
    const id = navigator.geolocation.watchPosition(
      pos => {
        const p = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        setStaffPos(p);
      },
      err => toast.error(`Live GPS: ${err.message}`),
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 5000 },
    );
    watchIdRef.current = id;
    return () => {
      navigator.geolocation.clearWatch(id);
      watchIdRef.current = null;
    };
  }, [liveTrack]);

  // -------- Auto-open + deviation detection --------
  useEffect(() => {
    if (!tripStarted || !staffPos || !displayOrder.length) return;
    const next = displayOrder[0];
    const distKm = haversineKm(staffPos, next);
    const distM = distKm * 1000;

    // 1) Auto-open when close enough (only fire once per task).
    if (distM <= PROXIMITY_M && lastAutoOpenRef.current !== next.id) {
      lastAutoOpenRef.current = next.id;
      toast.success(`You've arrived at ${next.name} — opening task…`);
      onOpenTask?.(next.task);
      distanceHistoryRef.current = [];
      return;
    }

    // 2) Trend detection — only when the staff has actually moved.
    const hist = distanceHistoryRef.current;
    const last = hist.length ? hist[hist.length - 1] : null;
    if (last != null && Math.abs(last - distKm) * 1000 < MOVE_EPSILON_M) {
      return;   // barely moved — ignore
    }
    hist.push(distKm);
    if (hist.length > DIVERGE_STREAK + 1) hist.shift();

    if (hist.length >= DIVERGE_STREAK + 1) {
      let increasing = true;
      for (let i = 1; i < hist.length; i++) {
        if (hist[i] <= hist[i - 1]) { increasing = false; break; }
      }
      if (increasing) {
        const trend = hist[hist.length - 1] - hist[0];
        toast.warning(
          `You're heading away from ${next.name} (+${trend.toFixed(2)} km). Ops has been notified.`,
          { duration: 6000 },
        );
        // Backend deviation alert (fire-and-forget)
        staffApi.post("/staff-app/route-deviation", {
          task_id: next.id,
          latitude: staffPos.lat,
          longitude: staffPos.lng,
          distance_km: distKm,
          trend_km: trend,
        }).catch(() => { /* swallow — non-blocking */ });
        distanceHistoryRef.current = [];  // reset streak
      }
    }
  }, [staffPos, tripStarted, displayOrder, onOpenTask]);

  // -------- Render markers + polyline --------
  useEffect(() => {
    if (!mapsReady || !mapRef.current) return;
    const G = window.google.maps;

    markersRef.current.forEach(m => m.setMap(null));
    markersRef.current = [];
    if (polylineRef.current) {
      polylineRef.current.setMap(null);
      polylineRef.current = null;
    }
    if (staffMarkerRef.current) {
      staffMarkerRef.current.setMap(null);
      staffMarkerRef.current = null;
    }

    const bounds = new G.LatLngBounds();

    // Staff marker — always-on live self-location (Iter 172). Green +
    // slightly bigger when a trip is under way; blue-solid otherwise.
    // A pulsing accent ring is drawn separately as a second circle so
    // the base marker stays sharp.
    if (staffPos) {
      staffMarkerRef.current = new G.Marker({
        position: staffPos,
        map: mapRef.current,
        title: tripStarted ? "You are here (live · trip)" : "You are here (live)",
        icon: {
          path: G.SymbolPath.CIRCLE,
          scale: tripStarted ? 12 : 10,
          fillColor: tripStarted ? "#2D5A27" : "#1F3A57",
          fillOpacity: 1,
          strokeColor: "#FFFFFF",
          strokeWeight: 3,
        },
        zIndex: 999,
      });
      // Pulsing accent ring — cosmetic, doesn't handle clicks.
      const pulse = new G.Circle({
        map: mapRef.current, center: staffPos,
        radius: 35, // meters
        strokeColor: tripStarted ? "#2D5A27" : "#1F3A57",
        strokeOpacity: 0.35, strokeWeight: 2,
        fillOpacity: 0, clickable: false,
        zIndex: 998,
      });
      markersRef.current.push(pulse);
      bounds.extend(staffPos);
    }

    displayOrder.forEach((s, i) => {
      const priColor = s.priority === "high"   ? "#7A2E18"
                      : s.priority === "medium" ? "#7A5A11"
                      :                          "#2D5A27";
      const marker = new G.Marker({
        position: { lat: s.lat, lng: s.lng },
        map: mapRef.current,
        label: {
          text: String(i + 1),
          color: "#FFFFFF",
          fontSize: "12px",
          fontWeight: "bold",
        },
        icon: {
          path: G.SymbolPath.CIRCLE,
          scale: 14,
          fillColor: priColor,
          fillOpacity: 1,
          strokeColor: "#FFFFFF",
          strokeWeight: 2,
        },
      });
      const info = new G.InfoWindow({
        content: `<div style="font-family:sans-serif;font-size:12px;padding:2px 4px;">
          <strong>${i + 1}. ${s.name}</strong><br/>
          <span style="color:#5C5A55;">${s.address || ""}</span><br/>
          <span style="color:#8A8780;font-size:10px;">${s.phone || ""}</span><br/>
          ${s.request_no ? `<span style="font-family:monospace;font-size:10px;color:#2D5A27;">${s.request_no}</span>` : ""}
        </div>`,
      });
      marker.addListener("click", () => info.open(mapRef.current, marker));
      markersRef.current.push(marker);
      bounds.extend({ lat: s.lat, lng: s.lng });
    });

    if (displayOrder.length > 1 || (staffPos && displayOrder.length > 0)) {
      const pathPts = [];
      if (staffPos) pathPts.push(staffPos);
      displayOrder.forEach(s => pathPts.push({ lat: s.lat, lng: s.lng }));
      polylineRef.current = new G.Polyline({
        path: pathPts,
        map: mapRef.current,
        strokeColor: "#C25D38",
        strokeOpacity: 0.9,
        strokeWeight: 3,
        icons: [{
          icon: { path: G.SymbolPath.FORWARD_CLOSED_ARROW, scale: 3 },
          offset: "50%",
          repeat: "80px",
        }],
      });
    }

    if (!bounds.isEmpty()) {
      // Iter 172 · Focus Near Me — when on, ignore the route bounds and
      // instead fit only the staff + visible nearby pins (radius-scaled).
      if (focusNearMe && staffPos) {
        const nearBounds = new G.LatLngBounds();
        nearBounds.extend(staffPos);
        const addAll = (arr) => arr.forEach(x => nearBounds.extend({ lat: x.lat, lng: x.lng }));
        if (layers.staff)     addAll(nearby.field_staff || []);
        if (layers.drops)     addAll(nearby.drop_locations || []);
        if (layers.godowns)   addAll(nearby.godowns || []);
        if (layers.customers) addAll(nearby.customers || []);
        mapRef.current.fitBounds(nearBounds, 80);
      } else {
        mapRef.current.fitBounds(bounds, 60);
      }
    }
  }, [mapsReady, staffPos, displayOrder, tripStarted, focusNearMe, layers, nearby]);

  // Iter 170 — render nearby overlays (other staff / drops / godowns /
  // customers) as coloured pins with an infoWindow that has a Navigate
  // button. Clears + redraws whenever the layers toggle or data reload.
  useEffect(() => {
    if (!mapsReady || !mapRef.current || !window.google?.maps) return;
    const G = window.google.maps;
    nearbyMarkersRef.current.forEach((m) => m.setMap(null));
    nearbyMarkersRef.current = [];
    const openNav = (lat, lng) => {
      const url = `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`
        + (staffPos ? `&origin=${staffPos.lat},${staffPos.lng}` : "");
      window.open(url, "_blank", "noopener");
    };
    const addPin = (item, cfg) => {
      const marker = new G.Marker({
        position: { lat: item.lat, lng: item.lng },
        map: mapRef.current,
        title: cfg.title(item),
        icon: {
          path: G.SymbolPath.CIRCLE,
          scale: 9,
          fillColor: cfg.color,
          fillOpacity: 0.9,
          strokeColor: "#FFFFFF",
          strokeWeight: 2,
        },
        zIndex: cfg.z,
      });
      const info = new G.InfoWindow({
        content: `<div style="font-family:sans-serif;font-size:12px;padding:4px 6px;min-width:180px;">
          <div style="font-weight:600;color:${cfg.color};">${cfg.label}</div>
          <div style="font-size:12px;">${cfg.title(item)}</div>
          <div style="color:#8A8780;font-size:10px;">${item.km != null ? item.km.toFixed(2) + " km away" : ""}</div>
          <button id="nav-${cfg.label}-${item.id || item.lat}" style="margin-top:6px;padding:4px 8px;background:#1F3A57;color:white;border:0;border-radius:4px;font-size:11px;cursor:pointer;">Navigate ↗</button>
        </div>`,
      });
      marker.addListener("click", () => {
        info.open(mapRef.current, marker);
        // Wire the navigate button once the DOM node is attached.
        G.event.addListenerOnce(info, "domready", () => {
          const btn = document.getElementById(`nav-${cfg.label}-${item.id || item.lat}`);
          if (btn) btn.onclick = () => openNav(item.lat, item.lng);
        });
      });
      nearbyMarkersRef.current.push(marker);
    };
    if (layers.staff) (nearby.field_staff || []).forEach((s) => addPin(s, {
      label: "Staff", color: "#1F3A57", z: 700,
      title: (x) => `${x.name || "Staff"} · ${x.role || ""}`.trim(),
    }));
    if (layers.drops) (nearby.drop_locations || []).forEach((d) => addPin(d, {
      label: "Drop", color: "#D4A017", z: 600,
      title: (x) => x.name || "Drop-off",
    }));
    if (layers.godowns) (nearby.godowns || []).forEach((d) => addPin(d, {
      label: "Godown", color: "#7A2E18", z: 650,
      title: (x) => x.name || "Godown",
    }));
    if (layers.customers) (nearby.customers || []).forEach((c) => addPin(c, {
      label: "Cust", color: "#3A7033", z: 550,
      title: (x) => `${x.name || "Customer"}${x.customer_code ? " · " + x.customer_code : ""}`,
    }));
  }, [mapsReady, layers, nearby, staffPos]);

  // -------- Route optimization --------
  const runOptimize = () => {
    if (!staffPos) {
      toast.error("Turn on location first — we optimize from where you are.");
      return;
    }
    if (eligible.length === 0) {
      toast.info("No pending stops with GPS to optimize.");
      return;
    }
    const ordered = optimizeRoute(staffPos, eligible);
    setOptimized(ordered);
    setRouteKm(null);   // reset — user can pull the road-distance again
    setRouteMin(null);
    toast.success(`Route optimized · ${ordered.length} stops`);
  };

  const clearOptimize = () => {
    setOptimized(null);
    setRouteKm(null);
    setRouteMin(null);
  };

  // -------- Fetch driving distance from Google Directions --------
  const fetchRouteDistance = useCallback(async () => {
    if (!staffPos || !displayOrder.length) return;
    if (!window.google?.maps?.DirectionsService) return;
    setRouteLoading(true);
    const svc = new window.google.maps.DirectionsService();
    // Google Directions caps waypoints at 25 (excluding origin/dest).
    const stops = displayOrder.slice(0, 25);
    const dest = stops[stops.length - 1];
    const waypoints = stops.slice(0, -1).map(s => ({
      location: { lat: s.lat, lng: s.lng }, stopover: true,
    }));
    try {
      const res = await svc.route({
        origin: staffPos,
        destination: { lat: dest.lat, lng: dest.lng },
        waypoints,
        travelMode: window.google.maps.TravelMode.DRIVING,
        optimizeWaypoints: false,
      });
      let m = 0, s = 0;
      (res.routes?.[0]?.legs || []).forEach(l => {
        m += l.distance?.value || 0;
        s += l.duration?.value || 0;
      });
      setRouteKm(m / 1000);
      setRouteMin(s / 60);
    } catch (e) {
      toast.error(`Directions failed: ${e.message || e}`);
    }
    setRouteLoading(false);
  }, [staffPos, displayOrder]);

  const startTrip = () => {
    if (!staffPos) { toast.error("Turn on location first"); return; }
    if (!displayOrder.length) { toast.error("No stops to visit"); return; }
    setTripStarted(true);
    setLiveTrack(true);
    distanceHistoryRef.current = [];
    lastAutoOpenRef.current = null;
    toast.success("Trip started — live tracking on");
  };

  const stopTrip = () => {
    setTripStarted(false);
    setLiveTrack(false);
    toast("Trip stopped · live tracking off");
  };

  const navigateTo = (stop) => {
    const url = stop.nav_url ||
      `https://www.google.com/maps/dir/?api=1&destination=${stop.lat},${stop.lng}` +
      (staffPos ? `&origin=${staffPos.lat},${staffPos.lng}` : "");
    window.open(url, "_blank", "noopener");
  };

  return (
    <div className="space-y-3" data-testid="staff-tasks-map-view">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2 px-3 py-2 bg-white rounded-md border border-[#E5E2D9]">
        <button
          onClick={locate}
          disabled={locating}
          data-testid="staff-map-locate"
          className="h-9 px-3 rounded-md text-white bg-[#1F3A57] hover:bg-[#26476A] disabled:opacity-60 text-[11px] font-semibold inline-flex items-center gap-1"
        >
          {locating
            ? <CircleNotch size={12} className="animate-spin"/>
            : <NavigationArrow size={12} weight="fill"/>}
          {locating ? "Locating…" : "My location"}
        </button>
        <button
          onClick={runOptimize}
          disabled={!staffPos || eligible.length === 0}
          data-testid="staff-map-optimize"
          className="h-9 px-3 rounded-md bg-[#C25D38] hover:bg-[#A54C2C] disabled:opacity-60 text-white text-[11px] font-semibold inline-flex items-center gap-1"
        >
          <Path size={12} weight="bold"/>
          Optimize route
        </button>
        {optimized && (
          <button
            onClick={fetchRouteDistance}
            disabled={routeLoading}
            data-testid="staff-map-route-km"
            className="h-9 px-3 rounded-md border border-[#2D5A27] text-[#2D5A27] hover:bg-[#EFF3EE] text-[11px] font-semibold inline-flex items-center gap-1 disabled:opacity-60">
            {routeLoading ? <CircleNotch size={12} className="animate-spin"/> : <Truck size={12}/>}
            {routeKm != null ? `Road ${routeKm.toFixed(1)} km` : "Load road distance"}
          </button>
        )}
        {optimized && (
          <button
            onClick={clearOptimize}
            data-testid="staff-map-clear"
            className="h-9 px-2 rounded-md border border-[#E5E2D9] hover:bg-[#F7F6F2] text-[11px] font-semibold text-[#5C5A55]"
          >
            Reset order
          </button>
        )}
        {!tripStarted ? (
          <button
            onClick={startTrip}
            disabled={!staffPos || !displayOrder.length}
            data-testid="staff-map-start-trip"
            className="h-9 px-3 rounded-md bg-[#2D5A27] hover:bg-[#3A7033] disabled:opacity-60 text-white text-[11px] font-semibold inline-flex items-center gap-1">
            <NavigationArrow size={12} weight="fill"/>Start trip
          </button>
        ) : (
          <button
            onClick={stopTrip}
            data-testid="staff-map-stop-trip"
            className="h-9 px-3 rounded-md bg-[#7A2E18] hover:bg-[#5C2213] text-white text-[11px] font-semibold inline-flex items-center gap-1">
            Stop trip
          </button>
        )}
        <div className="ml-auto text-[10px] text-[#5C5A55] flex items-center gap-1.5">
          <Truck size={12}/>
          {eligible.length} stop(s)
        </div>
      </div>

      {/* Iter 170 — nearby-overlay filter chips */}
      <div className="flex flex-wrap items-center gap-2 px-3 py-2 bg-white rounded-md border border-[#E5E2D9]"
           data-testid="staff-map-layer-chips">
        <span className="text-[10px] uppercase tracking-[0.14em] text-[#5C5A55] font-semibold mr-1">
          Show near me
        </span>
        {[
          { key: "staff",     label: "Other staff",    color: "#1F3A57", count: nearby.field_staff?.length || 0 },
          { key: "drops",     label: "Drop points",    color: "#D4A017", count: nearby.drop_locations?.length || 0 },
          { key: "godowns",   label: "Godowns",        color: "#7A2E18", count: nearby.godowns?.length || 0 },
          { key: "customers", label: "My customers",   color: "#3A7033", count: nearby.customers?.length || 0 },
        ].map(chip => (
          <button key={chip.key}
                  onClick={() => setLayers(l => ({ ...l, [chip.key]: !l[chip.key] }))}
                  data-testid={`staff-map-layer-${chip.key}`}
                  className={`h-8 px-2.5 rounded-full text-[11px] font-medium inline-flex items-center gap-1.5 border ${
                    layers[chip.key] ? "text-white border-transparent" : "bg-white text-[#5C5A55] border-[#E5E2D9]"
                  }`}
                  style={layers[chip.key] ? { background: chip.color } : {}}>
            <span className="inline-block w-2 h-2 rounded-full"
                  style={{ background: chip.color, boxShadow: layers[chip.key] ? "0 0 0 2px white inset" : "" }}/>
            {chip.label}
            <span className="opacity-80">{chip.count}</span>
          </button>
        ))}
        <div className="ml-auto flex items-center gap-1">
          <button onClick={() => setFocusNearMe(v => !v)}
                  data-testid="staff-map-focus-near"
                  className={`h-7 px-2 rounded-full text-[11px] font-medium border inline-flex items-center gap-1 ${
                    focusNearMe ? "bg-[#2D5A27] text-white border-[#2D5A27]" : "bg-white text-[#5C5A55] border-[#E5E2D9]"
                  }`}>
            <MapPin size={11} weight={focusNearMe ? "fill" : "regular"}/>
            Focus near me
          </button>
          <span className="text-[10px] uppercase tracking-[0.14em] text-[#8A8780] ml-2">Radius</span>
          {[2, 5, 10].map(r => (
            <button key={r}
                    onClick={() => setRadiusKm(r)}
                    data-testid={`staff-map-radius-${r}`}
                    className={`h-7 px-2 rounded text-[11px] font-medium border ${
                      radiusKm === r ? "bg-[#2D5A27] text-white border-[#2D5A27]" : "bg-white text-[#5C5A55] border-[#E5E2D9]"
                    }`}>{r} km</button>
          ))}
        </div>
      </div>

      {/* Distance summary */}
      {displayOrder.length > 0 && (
        <div className="flex flex-wrap items-center gap-3 px-3 py-2 bg-[#FAF8F2] rounded-md border border-[#E5E2D9] text-[11px]"
             data-testid="staff-map-distance-summary">
          <div>
            <span className="text-[#8A8780] uppercase tracking-[0.14em] mr-1">Straight-line</span>
            <b className="text-[#1F3A57]">{straightKm.toFixed(2)} km</b>
          </div>
          <div>
            <span className="text-[#8A8780] uppercase tracking-[0.14em] mr-1">Road</span>
            {routeKm != null ? (
              <>
                <b className="text-[#2D5A27]">{routeKm.toFixed(2)} km</b>
                {routeMin != null && (
                  <span className="text-[#5C5A55] ml-1">· ~{Math.round(routeMin)} min</span>
                )}
              </>
            ) : (
              <span className="text-[#8A8780]">— tap &ldquo;Load road distance&rdquo;</span>
            )}
          </div>
          {tripStarted && (
            <div className="ml-auto text-[#2D5A27] font-semibold inline-flex items-center gap-1">
              <span className="inline-block w-2 h-2 rounded-full bg-[#2D5A27] animate-pulse"/>
              Live tracking
            </div>
          )}
        </div>
      )}

      {/* Map */}
      <div className="relative w-full rounded-md overflow-hidden border border-[#E5E2D9] bg-[#F0EFEA]"
           style={{ height: 380 }}>
        {!mapsReady && (
          <div className="absolute inset-0 flex items-center justify-center text-[11px] text-[#5C5A55]">
            <CircleNotch size={16} className="animate-spin mr-1"/>Loading map…
          </div>
        )}
        <div ref={mapDivRef} className="w-full h-full"/>
      </div>

      {/* Optimized order preview list */}
      {optimized && optimized.length > 0 && (
        <div className="bg-white rounded-md border border-[#E5E2D9]"
             data-testid="staff-map-order-list">
          <div className="px-3 py-2 border-b border-[#F0EEE6] text-[10px] uppercase tracking-[0.16em] text-[#5C5A55] flex items-center gap-2">
            <span>Nearest-first order · auto-opens on arrival</span>
            {tripStarted && (
              <span className="ml-auto inline-flex items-center gap-1 text-[#2D5A27]">
                <Warning size={10} weight="fill"/>Alert if you head away
              </span>
            )}
          </div>
          <ol className="divide-y divide-[#F0EEE6]">
            {optimized.map((s, i) => {
              const isNext = tripStarted && i === 0;
              return (
                <li key={s.id}
                    data-testid={`staff-map-stop-${i}`}
                    className={`flex items-center gap-2 px-3 py-2 ${isNext ? "bg-[#EFF3EE]" : ""}`}>
                  <span className={`flex-shrink-0 w-6 h-6 rounded-full text-white text-[11px] font-bold flex items-center justify-center ${isNext ? "bg-[#2D5A27]" : "bg-[#C25D38]"}`}>
                    {i + 1}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="text-[13px] font-medium truncate">{s.name}</div>
                    <div className="text-[10px] text-[#8A8780] truncate">{s.address}</div>
                  </div>
                  <div className="text-[10px] text-[#5C5A55] whitespace-nowrap">
                    {s.leg_km != null ? `${s.leg_km.toFixed(1)} km` : ""}
                  </div>
                  {onOpenTask && (
                    <button
                      onClick={() => onOpenTask(s.task)}
                      data-testid={`staff-map-open-${s.id}`}
                      title="Open task detail"
                      className="text-[10px] uppercase tracking-[0.14em] font-semibold text-[#2D5A27] hover:underline">
                      Open
                    </button>
                  )}
                  <button
                    onClick={() => navigateTo(s)}
                    data-testid={`staff-map-nav-${s.id}`}
                    title="Navigate in Google Maps"
                    className="h-8 w-8 rounded-md bg-[#1F3A57] hover:bg-[#26476A] text-white flex items-center justify-center"
                  >
                    <NavigationArrow size={13} weight="fill"/>
                  </button>
                </li>
              );
            })}
          </ol>
        </div>
      )}

      {/* Legend */}
      <div className="text-[10px] text-[#8A8780] px-1 flex flex-wrap gap-3">
        <span className="inline-flex items-center gap-1">
          <MapPin size={10} className="text-[#7A2E18]" weight="fill"/> High priority
        </span>
        <span className="inline-flex items-center gap-1">
          <MapPin size={10} className="text-[#7A5A11]" weight="fill"/> Medium
        </span>
        <span className="inline-flex items-center gap-1">
          <MapPin size={10} className="text-[#2D5A27]" weight="fill"/> Low / normal
        </span>
        <span className="inline-flex items-center gap-1">
          <MapPin size={10} className="text-[#1F3A57]" weight="fill"/> Your start
        </span>
      </div>
    </div>
  );
}

// Re-export the toggle icons so StaffTasks.jsx can use them without an
// extra phosphor import.
export { MapTrifold, ListBullets };
